// FirstRunWelcome — the dismissible full-overlay welcome an SME sees the first
// time they land on /home (WP3, §2). Three short panes: what PRIME is + role
// confirmation, the guided-journey picker, and how to find your way around.
// It self-gates on route + onboarding state, so AppShell can mount it
// unconditionally.
//
// Tour launch is DOM-decoupled: buttons dispatch prime:start-tour (see
// tourEvents.js). The parallel tour engine listens; if it isn't mounted yet the
// click simply dismisses the welcome and nothing else happens — acceptable
// during parallel development.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useOnboardingState, dismissWelcome } from '../../lib/onboardingState.js';
import { personaKeyFromRoles, primaryRoleLabel } from '../../lib/personaKey.js';
import { wrapGlossaryTerms } from '../../components/primitives/GlossaryTerm.jsx';
import {
  whatIsPrime,
  personaCards,
  journeyCards,
  orientation,
  findingYourWay,
} from '../../content/welcomeContent.js';
import { startTour } from './tourEvents.js';
import './firstRunWelcome.css';

export function FirstRunWelcome() {
  const { user, roles = [] } = useCurrentUser();
  const userId = user?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const { welcomeDismissedAt } = useOnboardingState(userId);
  const [pane, setPane] = useState(1);
  const dialogRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const onHome = location.pathname === '/home';
  const forced = new URLSearchParams(location.search).has('welcome');
  const visible = onHome && (welcomeDismissedAt == null || forced);

  // Focus the dialog on open and restore focus to the previously-focused
  // element on close (simple focus trap per §2 accessibility).
  useEffect(() => {
    if (!visible) return undefined;
    restoreFocusRef.current = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [visible]);

  // Every dismissal path stamps welcomeDismissedAt and strips the ?welcome
  // replay param so the overlay closes (and stays closed on reload).
  function closeWelcome() {
    dismissWelcome(userId);
    if (location.search) navigate(location.pathname, { replace: true });
  }

  // Esc dismisses, matching the ConfirmActionDialog idiom.
  useEffect(() => {
    if (!visible) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') closeWelcome();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, location.search, location.pathname, userId]);

  if (!visible) return null;

  const personaKey = personaKeyFromRoles(roles);
  const display = personaKey ? rbac.PERSONA_DISPLAY[personaKey] : null;
  const card = personaKey ? personaCards[personaKey] : null;

  function launchTour(tourId) {
    closeWelcome();
    startTour(tourId);
  }

  return (
    <div
      className="first-run-welcome__scrim"
      data-testid="first-run-welcome"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeWelcome();
      }}
    >
      <div
        ref={dialogRef}
        className="first-run-welcome glass-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="first-run-welcome-title"
        tabIndex={-1}
      >
        <header className="first-run-welcome__head">
          <div className="first-run-welcome__brand">
            <span className="first-run-welcome__kicker">Welcome to</span>
            <span className="first-run-welcome__wordmark">DHA PRIME</span>
          </div>
          <button
            type="button"
            className="first-run-welcome__close"
            data-testid="welcome-dismiss"
            onClick={closeWelcome}
            aria-label="Close welcome"
          >
            <span aria-hidden>×</span>
          </button>
        </header>

        <ol className="first-run-welcome__progress" aria-hidden>
          {[1, 2, 3].map((n) => (
            <li key={n} className={n === pane ? 'is-current' : n < pane ? 'is-done' : ''} />
          ))}
        </ol>

        {pane === 1 ? (
          <section className="first-run-welcome__pane" data-testid="welcome-pane-1">
            <h2 id="first-run-welcome-title" className="first-run-welcome__title">
              What is PRIME?
            </h2>
            <p className="first-run-welcome__lede">{wrapGlossaryTerms(whatIsPrime)}</p>

            <div className="first-run-welcome__role-card" data-testid="welcome-role-card">
              <div className="first-run-welcome__role-eyebrow">You&rsquo;re signed in as</div>
              <div className="first-run-welcome__role-name">
                {/* The account's actual role, not just the persona bucket it
                    maps to — a Program Manager must be greeted as one (DEF-002). */}
                {primaryRoleLabel(roles) || display?.label || 'A PRIME user'}
              </div>
              <p className="first-run-welcome__role-summary">
                {card?.plainSummary ||
                  'Explore the workspace to see what your role can do in PRIME.'}
              </p>
            </div>

            <div className="first-run-welcome__actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => navigate('/login')}
              >
                Switch role
              </button>
              <button type="button" className="btn primary" onClick={() => setPane(2)}>
                That&rsquo;s me — start here
              </button>
            </div>
          </section>
        ) : null}

        {pane === 2 ? (
          <section className="first-run-welcome__pane" data-testid="welcome-pane-2">
            <h2 id="first-run-welcome-title" className="first-run-welcome__title">
              Start here
            </h2>
            <p className="first-run-welcome__lede">
              Pick a short guided walkthrough, or take the quick orientation first. You can always
              reopen these later.
            </p>

            <button
              type="button"
              className="first-run-welcome__orientation"
              data-testid={`welcome-journey-${orientation.tourId}`}
              onClick={() => launchTour(orientation.tourId)}
            >
              <span className="first-run-welcome__orientation-title">{orientation.title}</span>
              <span className="first-run-welcome__orientation-blurb">{orientation.blurb}</span>
            </button>

            <ul className="first-run-welcome__journeys">
              {journeyCards.map((journey) => (
                <li key={journey.tourId}>
                  <button
                    type="button"
                    className="first-run-welcome__journey"
                    data-testid={`welcome-journey-${journey.tourId}`}
                    onClick={() => launchTour(journey.tourId)}
                  >
                    <span className="first-run-welcome__journey-top">
                      <span className="first-run-welcome__journey-title">{journey.title}</span>
                      <span className="first-run-welcome__journey-minutes">
                        {journey.minutes} min
                      </span>
                    </span>
                    <span className="first-run-welcome__journey-blurb">{journey.blurb}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="first-run-welcome__actions">
              <button type="button" className="btn ghost" onClick={() => setPane(1)}>
                Back
              </button>
              <button type="button" className="btn secondary" onClick={() => setPane(3)}>
                Finding your way
              </button>
            </div>
          </section>
        ) : null}

        {pane === 3 ? (
          <section className="first-run-welcome__pane" data-testid="welcome-pane-3">
            <h2 id="first-run-welcome-title" className="first-run-welcome__title">
              {findingYourWay.heading}
            </h2>
            <p className="first-run-welcome__lede">{findingYourWay.intro}</p>
            <ul className="first-run-welcome__tips">
              <li>{findingYourWay.helpPointer}</li>
              <li>{findingYourWay.askPointer}</li>
            </ul>

            <div className="first-run-welcome__actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  closeWelcome();
                }}
              >
                Explore on my own
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  closeWelcome();
                  navigate('/help');
                }}
              >
                Open Help &amp; getting started
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
